import React, { useEffect, useMemo } from 'react';
import { Box, Button, Grid } from '@mui/material';
import { FilterAlt } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import Dropdown from 'components/Dropdown';
import Input from 'components/Input';
import InputMask from 'components/InputMask';
import VirtualDrop from 'components/VirtualDrop';
import styles from './styles';
import { getFilterItems, getInitialFilterItemKey } from './utils';

const renderField = ({ field, form }) => {
  const { control } = form;

  if (field?.render) {
    return field.render({ form, field });
  }

  if (field?.type === 'select') {
    return (
      <Dropdown
        key={field.name}
        name={field.name}
        control={control}
        label={field.label}
        options={field.options}
        multiple={field.multiple}
        disabled={field.disabled}
      />
    );
  }

  if (field?.type === 'virtual-select') {
    return (
      <VirtualDrop
        key={field.name}
        name={field.name}
        control={control}
        label={field.label}
        options={field.options}
        multiple={field.multiple}
        refresh={field.refresh}
        busca_refinada={field.busca_refinada}
        disabled={field.disabled}
      />
    );
  }

  if (field?.type === 'date') {
    return (
      <InputMask
        key={field.name}
        name={field.name}
        control={control}
        label={field.label}
        type="date"
        disabled={field.disabled}
      />
    );
  }

  if (field?.type === 'currency') {
    return (
      <InputMask
        key={field.name}
        name={field.name}
        control={control}
        label={field.label}
        disabled={field.disabled}
      />
    );
  }

  return (
    <Input
      key={field.name}
      name={field.name}
      control={control}
      label={field.label}
      type={field.type === 'number' ? 'number' : undefined}
      disabled={field.disabled}
    />
  );
};

const renderFilterItem = ({ item, form }) => {
  if (item?.type !== 'range') {
    return renderField({ field: item.field, form });
  }

  return (
    <Grid container spacing={1}>
      {item?.fields?.map((field) => (
        <Grid item xs={12} sm={6} key={field.name}>
          {renderField({ field, form })}
        </Grid>
      ))}
    </Grid>
  );
};

const FilterBar = ({
  fields = [],
  form,
  grid,
  loading,
  onSubmit,
}) => {
  const selectorForm = useForm({
    defaultValues: {
      field: '',
    },
  });
  const selectedField = selectorForm.watch('field');
  const filterItems = useMemo(
    () => getFilterItems(fields, grid),
    [fields, grid]
  );
  const fieldOptions = useMemo(
    () =>
      filterItems?.map((item) => ({
        value: item.key,
        label: item.label,
      })),
    [filterItems]
  );
  const activeItem = filterItems?.find((item) => item.key === selectedField);

  useEffect(() => {
    if (!filterItems?.length) {
      selectorForm.setValue('field', '');
      return;
    }

    if (!filterItems?.some((item) => item.key === selectedField)) {
      selectorForm.setValue('field', getInitialFilterItemKey(filterItems));
    }
  }, [selectedField, selectorForm, filterItems]);

  if (!form || !filterItems?.length) {
    return null;
  }

  const handleSubmit = form.handleSubmit(() => {
    if (onSubmit) {
      onSubmit();
    }
  });

  return (
    <Box component="form" sx={styles.filterBar} onSubmit={handleSubmit}>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} sm={4} lg={4}>
          <Dropdown
            name="field"
            control={selectorForm.control}
            label="Buscar por"
            options={fieldOptions}
          />
        </Grid>
        <Grid item xs={12} sm={8} lg={8}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm key={activeItem?.key}>
              {activeItem && renderFilterItem({ item: activeItem, form })}
            </Grid>
            <Grid item xs={12} sm="auto">
              <Button
                type="submit"
                size="small"
                variant="contained"
                startIcon={<FilterAlt />}
                disabled={loading}
                sx={styles.filterSubmit}
              >
                Filtrar
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FilterBar;
